import { type Selection, pointer, BaseType } from "d3";
import { useEffect, useRef, useState } from "react";

import { Cartesian } from "@examspark/types";

import { D3Axis, roundToGrid } from "../d3Utils";
import { areCoordinatesEqual } from "../utils";

import { createCoordinate } from "./drawShapes";

type UseListenerPointsProps = {
  mode?: string;
  shapes?: Cartesian.Shape[];
  onChange?: (shapes: Cartesian.Shape[]) => void;
};

const useListenerPoints = (props: UseListenerPointsProps) => {
  const [listenPoints, setListenPoints] = useState<Cartesian.Coordinate[]>([]);

  // clear listen points when mode changes
  const lastMode = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (lastMode.current === props.mode) return;
    lastMode.current = props.mode;

    setListenPoints([]);
  }, [props.mode]);

  useEffect(() => {
    if (!listenPoints) return;

    switch (props.mode) {
      case "point":
        if (listenPoints.length > 0) {
          props.onChange?.(
            (props.shapes || []).concat({
              type: "point",
              coord: listenPoints[0],
            }),
          );
          setListenPoints([]);
        }
        break;
      case "circle":
        if (listenPoints.length === 2) {
          props.onChange?.(
            (props.shapes || []).concat({
              type: "circle",
              coords: listenPoints,
            }),
          );
          setListenPoints([]);
        }
        break;
      case "ray":
      case "dashed-line":
      case "line":
        if (listenPoints.length === 2) {
          props.onChange?.(
            (props.shapes || []).concat({
              type: props.mode,
              coords: listenPoints,
            }),
          );
          setListenPoints([]);
        }
        break;
      case "line-segment":
        if (listenPoints.length === 2) {
          props.onChange?.(
            (props.shapes || []).concat({
              type: "line-segment",
              coords: listenPoints,
            }),
          );
          setListenPoints([]);
        }
        break;
      case "polygon":
        if (listenPoints.length > 2) {
          if (
            areCoordinatesEqual(
              listenPoints[0],
              listenPoints[listenPoints.length - 1],
            )
          ) {
            // users almost certainly don't want overlapping points they can't see
            const uniqueListenPoints = new Map<
              `${number},${number}`,
              Cartesian.Coordinate
            >();
            listenPoints.forEach((point) => {
              uniqueListenPoints.set(`${point.x},${point.y}`, point);
            });
            props.onChange?.(
              (props.shapes || []).concat({
                type: "polygon",
                coords: [...uniqueListenPoints.values()],
              }),
            );
            setListenPoints([]);
          }
        }
        break;
    }
  }, [listenPoints, props]);

  return [listenPoints, setListenPoints] as const;
};

export const useListenPointsDecorator = <GType extends BaseType>({
  mode,
  x,
  y,
  shapes,
  onChange,
}: {
  x: D3Axis;
  y: D3Axis;
  mode: string | undefined;
  shapes: Cartesian.Shape[];
  onChange?: (shapes: Cartesian.Shape[]) => void;
}) => {
  const [listenPoints, setListenPoints] = useListenerPoints({
    mode,
    shapes,
    onChange,
  });

  return (svg: Selection<GType, unknown, null, undefined>) => {
    const should_render = !!mode && mode !== "erase" && mode !== "answer";

    const listen_points = svg
      .selectAll("g.graph-listen-points")
      // passing an empty array here will remove the element
      .data([should_render].filter(Boolean))
      .join("g")
      .attr("class", "graph-listen-points");

    listen_points
      .selectAll("circle.graph-listen-point")
      .data(listenPoints)
      .join("circle")
      .attr("class", "graph-listen-point")
      .call(createCoordinate({ x, y }))
      .attr("fill", "red");

    listen_points
      .selectAll("circle.graph-listen-point-center")
      .data(listenPoints)
      .join("circle")
      .attr("class", "graph-listen-point-center")
      .attr("r", 4)
      .attr("cx", (coord) => x.scale(coord.x))
      .attr("cy", (coord) => y.scale(coord.y))
      .attr("fill", "red");

    const handleCreateShapeClick = (e: React.MouseEvent<SVGElement>) => {
      const [pointerX, pointerY] = pointer(e);

      setListenPoints((listenPoints) =>
        listenPoints.concat({
          x: roundToGrid(x.scale.invert(pointerX), x),
          y: roundToGrid(y.scale.invert(pointerY), y),
        }),
      );
    };

    svg.selectAll(".graph-interaction").remove();

    if (should_render) {
      svg
        .append("rect")
        .attr("class", "graph-interaction")
        .attr("width", "100%")
        .attr("height", "100%")
        .attr("fill", "transparent")
        .on("click", handleCreateShapeClick);
    }
  };
};
